import Quill from 'quill'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Block = Quill.import('blots/block') as any

/**
 * Blot customizado para criar itens de checklist estilo GitHub
 * Sintaxe Markdown: [ ] para não marcado, [x] para marcado
 */
class ChecklistItem extends Block {
  static blotName = 'checklist-item'
  static tagName = 'LI'
  static className = 'ql-checklist-item'

  static create(value: boolean | string) {
    const node = super.create() as HTMLElement
    const isChecked = value === true || value === 'true' || value === 'checked'
    node.setAttribute('data-checked', isChecked ? 'true' : 'false')
    node.classList.add('ql-checklist-item')
    if (isChecked) {
      node.classList.add('checked')
    }
    return node
  }

  static formats(node: HTMLElement) {
    return node.getAttribute('data-checked') === 'true'
  }

  format(name: string, value: boolean | string) {
    if (name === ChecklistItem.blotName) {
      const isChecked = value === true || value === 'true' || value === 'checked'
      this.domNode.setAttribute('data-checked', isChecked ? 'true' : 'false')
      if (isChecked) {
        this.domNode.classList.add('checked')
      } else {
        this.domNode.classList.remove('checked')
      }
    } else {
      super.format(name, value)
    }
  }
}

// Registrar o blot
Quill.register(ChecklistItem, true)

/**
 * Inicializa o módulo de checklist no Quill
 * Adiciona handler de clique para marcar/desmarcar checkboxes
 */
export function initChecklistModule(quill: Quill, onChange?: () => void) {
  const editorRoot = quill.root

  // Handler de clique para toggle do checkbox
  editorRoot.addEventListener('click', (e: MouseEvent) => {
    const target = e.target as HTMLElement
    
    // Verificar se clicou em um item de checklist
    if (target.classList.contains('ql-checklist-item')) {
      const rect = target.getBoundingClientRect()
      // Verificar se o clique foi na área do checkbox (primeiros 28px)
      if (e.clientX - rect.left < 28) {
        e.preventDefault()
        e.stopPropagation()
        
        const isChecked = target.getAttribute('data-checked') === 'true'
        target.setAttribute('data-checked', (!isChecked).toString())
        target.classList.toggle('checked', !isChecked)
        
        // Disparar evento de mudança no Quill
        quill.update('user')
        onChange?.()
      }
    }
  })

  // Adicionar handler para o botão de checklist na toolbar
  const toolbar = quill.getModule('toolbar')
  if (toolbar && typeof toolbar === 'object' && 'addHandler' in toolbar) {
    const tb = toolbar as { addHandler: (name: string, handler: () => void) => void }
    tb.addHandler('checklist', () => {
      const range = quill.getSelection()
      if (range) {
        const format = quill.getFormat(range)
        const isChecklist = format['checklist-item'] !== undefined
        
        if (isChecklist) {
          // Remove o formato de checklist
          quill.format('checklist-item', false)
        } else {
          // Adiciona o formato de checklist (não marcado por padrão)
          quill.format('checklist-item', false)
        }
      }
    })
  }
}

/**
 * Converte texto com sintaxe markdown de checkbox para HTML
 * [ ] -> checkbox não marcado
 * [x] ou [X] -> checkbox marcado
 */
export function parseChecklistMarkdown(html: string): string {
  // Padrão para encontrar linhas começando com [ ] ou [x]
  const checkboxPattern = /^(\s*)\[( |x|X)\]\s*(.*)$/gm
  
  return html.replace(checkboxPattern, (_match, _indent, checked, text) => {
    const isChecked = checked.toLowerCase() === 'x'
    return `<li class="ql-checklist-item${isChecked ? ' checked' : ''}" data-checked="${isChecked}">${text}</li>`
  })
}

/**
 * Converte HTML de checklist de volta para sintaxe markdown
 */
export function checklistToMarkdown(html: string): string {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  
  const checklistItems = doc.querySelectorAll('.ql-checklist-item')
  checklistItems.forEach((item) => {
    const isChecked = item.getAttribute('data-checked') === 'true'
    const text = item.textContent || ''
    const markdown = isChecked ? `[x] ${text}` : `[ ] ${text}`
    item.replaceWith(document.createTextNode(markdown))
  })
  
  return doc.body.innerHTML
}

export { ChecklistItem }
